/*
 * CHI-29: ONE events.myTickets() call grouped by eventSlug (no per-tile N+1 query).
 * Only webhook-issued ACTIVE tickets count; an unpaid / registration-only order has none.
 * blockingSlugs (CHI-22): Dev2's blockingEventSlugs (active OR suspended) from the same call;
 * if the field is missing, falls back to the slugs with an active ticket.
 */
import { useCallback, useEffect, useState } from "react";
import { useTicketingApiWiring } from "@/lib/ticketingApiProvider";
import { getTicketingApi, type Ticket } from "@/lib/ticketingApi";
import { onTicketsConfirmed } from "@/lib/ticketsConfirmed";

export function groupActiveTicketsBySlug(tickets: Ticket[]): Map<string, Ticket[]> {
  const out = new Map<string, Ticket[]>();
  const seen = new Set<number>();
  for (const t of tickets) {
    if (t.status !== "active" || seen.has(t.ticketId)) continue;
    seen.add(t.ticketId);
    out.set(t.eventSlug, [...(out.get(t.eventSlug) ?? []), t]);
  }
  return out;
}

export function blockingSlugsFrom(res: { tickets?: Ticket[] | null; blockingEventSlugs?: string[] | null } | null | undefined): Set<string> {
  if (!res) return new Set();
  if (Array.isArray(res.blockingEventSlugs)) return new Set(res.blockingEventSlugs);
  return new Set(groupActiveTicketsBySlug(res.tickets ?? []).keys());
}

export function useMyTicketsBySlug(enabled: boolean) {
  useTicketingApiWiring();
  const [state, setState] = useState<{ status: "idle" | "loading" | "ok" | "error"; bySlug: Map<string, Ticket[]>; blockingSlugs: Set<string>; serverNow?: string }>({ status: "idle", bySlug: new Map(), blockingSlugs: new Set() });
  const load = useCallback(async () => {
    if (!enabled) return;
    setState(s => ({ ...s, status: s.status === "ok" ? "ok" : "loading" }));
    try {
      const res = await getTicketingApi().myTickets();
      setState({ status: "ok", bySlug: groupActiveTicketsBySlug(res.tickets ?? []), blockingSlugs: blockingSlugsFrom(res), serverNow: res.serverNow });
    } catch {
      setState({ status: "error", bySlug: new Map(), blockingSlugs: new Set() });
    }
  }, [enabled]);
  useEffect(() => {
    if (!enabled) { setState({ status: "idle", bySlug: new Map(), blockingSlugs: new Set() }); return; }
    void load();
  }, [enabled, load]);
  // CHI-27: refetch when the return page confirms an order's tickets.
  useEffect(() => (enabled ? onTicketsConfirmed(() => void load()) : undefined), [enabled, load]);
  const hasBlockingTicket = useCallback((slug: string) => state.blockingSlugs.has(slug), [state.blockingSlugs]);
  return { ...state, hasBlockingTicket, reload: load };
}
